import React, { useState, useRef, useEffect, useContext } from "react";

//Import Mui
import {
  Box,
  Button,
  CardContent,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import MuiTextField from "../../controls/MuiTextField";
import MuiAutoComplete from "../../controls/MuiAutoComplete";
import MuiDatePicker from "../../controls/MuiDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import Alert from "../../controls/MuiSnackbarAlert";

//Import Redux
import { useDispatch, useSelector } from "react-redux";

//Import Reduxtoolkit
import { unwrapResult } from "@reduxjs/toolkit";

//Import from customerSlice.js
import {
  getCustomersFromServer,
  selectAllCustomers,
} from "../../masters/customer/customerSlice";

//Import from summarySlice.js
import {
  getSummaryDetailsFromServer,
  getSummaryDetailsFromServerByPagination,
} from "./summaryReportSlice";

// Import Excel & PDF
import jsPDF from "jspdf";
import "jspdf-autotable";

import { getCurrentFormattedDate } from "../../../app/constants";

//Import from Assets
import logo from "../../../assets/MARFish.png";

//Import CSS
import "./summaryReport.css";

const SummaryReport = () => {
  const dispatch = useDispatch();
  const customersList = useSelector(selectAllCustomers);
  const currentDate = getCurrentFormattedDate();

  //useState
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerCode, setCustomerCode] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [tableData, setTableData] = useState([]);
  const [originalSummaryList, setOriginalSummaryList] = useState([]);
  const [summaryCount, setSummaryCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentAmount, setCurrentAmount] = useState("");
  const [lessAmount, setLessAmount] = useState("");
  const [collectionAmount, setCollectionAmount] = useState("");
  const [openingBalance, setOpeningBalance] = useState("");
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    severity: "",
  });
  //End useState

  //useRef
  const customersRef = useRef(null);
  const searchRef = useRef(null);
  const printRef = useRef(null);
  const refreshRef = useRef(null);
  //End useRef

  const fetchDatasByPagination = async (pageNo, rowSize) => {
    try {
      const result = await dispatch(
        getSummaryDetailsFromServerByPagination({
          customerCode,
          pageNo,
          rowSize,
        })
      );
      const unwrappedResult = unwrapResult(result);
      if (unwrappedResult.data.length == 0) {
        setNotify({
          isOpen: true,
          message: result.message,
          severity: "warning",
        });
      } else {
        // Flatten the data for the table
        let flattenedData = [];
        unwrappedResult.data.forEach((summary) => {
          summary.SummaryDetails.forEach((detail, index) => {
            flattenedData.push({
              key: `${detail.SalesNo}-${index}`, // unique key for each row
              SalesNo: detail.SalesNo,
              SalesDate: detail.SalesDate,
              CurrentAmount: detail.CurrentAmount,
              LessAmount: detail.LessAmount,
              CollectionAmount: detail.CollectionAmount,
            });
          });
        });
        setCurrentAmount(unwrappedResult.data[0].SumofCurrentAmount);
        setLessAmount(unwrappedResult.data[0].SumofLessAmount);
        setCollectionAmount(unwrappedResult.data[0].SumofCollectionAmount);
        setOpeningBalance(unwrappedResult.data[0].OpeningBalance);
        setTableData(flattenedData);
      }
    } catch (err) {
      console.log({ ErrorOccured: err });
      setNotify({
        isOpen: true,
        message: err.message,
        severity: "error",
      });
    }
  };

  //useEffect
  useEffect(() => {
    // if (customersList !== undefined && customersList.length > 0) {
    //   setCustomers(customersList);
    // } else {
    dispatch(getCustomersFromServer())
      .then(unwrapResult)
      .then((result) => {
        if (result.data.length > 0) {
          setCustomers(result.data);
        }
      })
      .catch((err) => {
        console.log({ ErrorOccured: err });
        setNotify({
          isOpen: true,
          message: err.message,
          severity: "error",
        });
      });
    // }
  }, []);
  //End useEffect

  const handleCustomerChange = async (e, value) => {
    e.preventDefault();
    setSelectedCustomer(value);
    if (value) {
      setCustomerCode(value.CUCode);
      setCustomerName(value.CUName);
      // Remove commas from the OpeningBalance string and convert it to a number
      // const cleanOpeningBalance = parseFloat(
      //   value.OpeningBalance.replace(/,/g, "")
      // );
      searchRef.current.focus();
    }
  };

  const columns = [
    { id: "SalesNo", label: "Sales No", minWidth: 100 },
    { id: "SalesDate", label: "Sales Date", minWidth: 100 },
    {
      id: "CurrentAmount",
      label: "Current Amount",
      align: "right",
      minWidth: 170,
    },
    {
      id: "LessAmount",
      label: "Less Amount",
      align: "right",
      minWidth: 170,
    },
    {
      id: "CollectionAmount",
      label: "Collection Amount",
      align: "right",
      minWidth: 170,
    },
  ];

  const handleSearch = async (e) => {
    e.preventDefault();
    if (selectedCustomer === null) {
      setNotify({
        isOpen: true,
        message: "Customer is required",
        severity: "warning",
      });
      customersRef.current.focus();
      return;
    } else {
      await fetchDatasByPagination(page + 1, rowsPerPage);
      await dispatch(getSummaryDetailsFromServer(customerCode))
        .then(unwrapResult)
        .then((result) => {
          if (result.data.length === 0) {
            setOriginalSummaryList([]);
            setSummaryCount(0);
          } else {
            // Flatten the data for the table
            let flattenedData = [];
            result.data.forEach((summary) => {
              summary.SummaryDetails.forEach((detail, index) => {
                flattenedData.push({
                  key: `${detail.SalesNo}-${index}`, // unique key for each row
                  SalesNo: detail.SalesNo,
                  SalesDate: detail.SalesDate,
                  CurrentAmount: detail.CurrentAmount,
                  LessAmount: detail.LessAmount,
                  CollectionAmount: detail.CollectionAmount,
                });
              });
            });
            // setCurrentAmount(result.data[0].SumofCurrentAmount);
            // setLessAmount(result.data[0].SumofLessAmount);
            // setCollectionAmount(result.data[0].SumofCollectionAmount);
            // setOpeningBalance(result.data[0].OpeningBalance);
            setOriginalSummaryList(flattenedData);
            setSummaryCount(flattenedData.length);
          }
        })
        .catch((err) => {
          console.log({ ErrorOccured: err });
          setNotify({
            isOpen: true,
            message: err.message,
            severity: "error",
          });
        });
    }
  };

  const handlePrint = (e) => {
    e.preventDefault();
    if (selectedCustomer === null) {
      setNotify({
        isOpen: true,
        message: "Customer is required",
        severity: "warning",
      });
      customersRef.current.focus();
      return;
    } else if (tableData && tableData.length === 0) {
      setNotify({
        isOpen: true,
        message: "Summary details not found, please click on Search",
        severity: "warning",
      });
      return;
    } else {
      const doc = new jsPDF();

      // Load and add the image
      const imgData = logo; // Image path from your assets folder
      doc.addImage(imgData, "PNG", 90, 5, 30, 20); // Adjust the position (x, y) and size (width, height)

      // Add the address details below the image
      const address = `
            MAR & SONS FISH COMPANY
      ShopNo:7, Sandhaipettai Fish Market,
      Thennampalayam, Tirupur-641 604.
      9952420505, 9500951327, 9894777666`;

      doc.setFontSize(10); // Set font size for address text
      doc.text(address, 70, 28); // Adjust position (x, y) as necessary

      const tableColumn = columns.slice(0, 5).map((col) => col.label);

      //   const tableRows = tableData.map((item) => {
      //     console.log(item);
      //   });

      const tableRows = originalSummaryList.map((summary) =>
        columns.slice(0, 5).map((col) => summary[col.id])
      );

      // Find the indices of the 'Opening Balance' and 'Collection Amount' columns
      const currentAmountIndex = tableColumn.indexOf("Current Amount");
      const lessAmountIndex = tableColumn.indexOf("Less Amount");
      const collectionAmountIndex = tableColumn.indexOf("Collection Amount");

      // Add the table below the address
      doc.autoTable({
        startY: 50,
        head: [tableColumn],
        body: tableRows,
        margin: { top: 10 },
        styles: { overflow: "linebreak" },
        headStyles: { fontSize: 10 },
        columnStyles: {
          [currentAmountIndex]: { halign: "right" },
          [lessAmountIndex]: { halign: "right" },
          [collectionAmountIndex]: { halign: "right" },
        },
        didParseCell: function (data) {
          // Check if the cell is a header cell
          if (data.section === "head") {
            if (
              data.column.index === currentAmountIndex ||
              data.column.index === lessAmountIndex ||
              data.column.index === collectionAmountIndex
            ) {
              data.cell.styles.halign = "right"; // Align specific header cells to the right
            }
          }
        },
      });

      // Add totals to the last page
      const labelWidth = 120; // Width for the label column
      const valueWidth = 120; // Width for the value column

      // Add totals to the last page
      doc.autoTable({
        theme: "plain",
        startY: doc.autoTable.previous.finalY + 10, // Start below the last table
        head: [["Totals"]],
        body: [
          ["Current Amount    " + " : " + currentAmount],
          ["Less Amount       " + "  :        " + lessAmount],
          ["Collection Amount " + ": " + collectionAmount],
          ["Opening Balance  " + ": " + openingBalance],
        ],
        styles: { fontSize: 10, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: labelWidth, halign: "left" },
          // 1: { cellWidth: valueWidth, halign: "right" },
        },
      });

      // Save the PDF
      doc.save("SummaryReport_" + currentDate + ".pdf");
    }
  };

  const handleRefresh = (e) => {
    e.preventDefault();
    window.location.reload();
  };

  const handleChangePage = (event, newPage) => {
    event.preventDefault();
    setPage(newPage);
    fetchDatasByPagination(newPage + 1, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setPage(0);
    fetchDatasByPagination(page + 1, event.target.value);
  };

  return (
    <>
      <Alert notify={notify} setNotify={setNotify} />
      <Box className="summaryreportboxcontainer">
        <Paper className="paper" elevation={2} sx={{ p: 2 }}>
          {/* <h6>Add Customer</h6>
          <Divider /> */}
          <CardContent>
            <Grid container spacing={2} className="gridContainer">
              <Grid item xs={12} sm={6} md={3}>
                <MuiAutoComplete
                  id="customer"
                  clearOnEscape
                  autoHighlight
                  autoSelect
                  options={customers}
                  getOptionLabel={(option) =>
                    `${option.CUCode} - ${option.CUName}` || ""
                  }
                  value={selectedCustomer}
                  onChange={handleCustomerChange}
                  renderInput={(params) => (
                    <MuiTextField
                      {...params}
                      label="Customer *"
                      inputRef={customersRef}
                      autoFocus
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={1}>
                <Button
                  className="btnsearch"
                  variant="contained"
                  size="small"
                  ref={searchRef}
                  onKeyDown={(e) => {
                    if (e.key.toLowerCase() === "home")
                      customersRef.current.focus();
                  }}
                  onClick={handleSearch}
                >
                  Search
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={1}>
                <Button
                  variant="contained"
                  size="small"
                  className="btnprint"
                  ref={printRef}
                  onKeyDown={(e) => {
                    if (e.key.toLowerCase() === "home")
                      searchRef.current.focus();
                  }}
                  onClick={handlePrint}
                >
                  Print
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={1}>
                <Button
                  className="btnrefresh"
                  variant="contained"
                  size="small"
                  ref={refreshRef}
                  onKeyDown={(e) => {
                    if (e.key.toLowerCase() === "home")
                      printRef.current.focus();
                  }}
                  onClick={handleRefresh}
                >
                  Refresh
                </Button>
              </Grid>
              <Grid item xs={12}></Grid>
              <TableContainer sx={{ maxHeight: 1000 }}>
                <Table stickyHeader aria-label="sticky table">
                  <TableHead>
                    <TableRow>
                      {columns.map((column) => (
                        <TableCell
                          key={column.id}
                          align={column.align}
                          style={{ minWidth: column.minWidth }}
                        >
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableData.length > 0 ? (
                      tableData.map((summary) => (
                        <TableRow key={summary.key}>
                          <TableCell>{summary.SalesNo}</TableCell>
                          <TableCell>{summary.SalesDate}</TableCell>
                          <TableCell align="right">
                            {summary.CurrentAmount}
                          </TableCell>
                          <TableCell align="right">
                            {summary.LessAmount}
                          </TableCell>
                          <TableCell align="right">
                            {summary.CollectionAmount}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={columns.length} align="center">
                          customer Summary not found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                className="MuiTablePagination-selectLabel"
                rowsPerPageOptions={[10, 25, 50, 75, 100]}
                component="div"
                count={summaryCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Grid>
          </CardContent>
        </Paper>
      </Box>
      <Box className="summaryreportboxcontainer">
        <Paper className="paper" elevation={2} sx={{ p: 2 }}>
          <CardContent>
            <Grid container spacing={2} className="gridContainer">
              <Grid item xs={12} sm={6} md={3}>
                <MuiTextField
                  type="number"
                  id="currentamount"
                  label="Current Amount"
                  InputProps={{
                    readOnly: true,
                  }}
                  onChange={(e) => {
                    //not allow to enter negative values - /^\d*\.?\d*$/
                    //not allow to enter negative values & allow only 2 digits after decimal - /^\d*\.?\d{0,2}$/
                    const newValue = e.target.value;
                    if (!newValue || newValue.match(/^\d*\.?\d{0,2}$/)) {
                      setCurrentAmount(parseFloat(newValue));
                    }
                  }}
                  value={isNaN(currentAmount) ? "" : currentAmount}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MuiTextField
                  type="number"
                  id="lessamount"
                  label="Less Amount"
                  onChange={(e) => {
                    //not allow to enter negative values - /^\d*\.?\d*$/
                    //not allow to enter negative values & allow only 2 digits after decimal - /^\d*\.?\d{0,2}$/
                    const newValue = e.target.value;
                    if (!newValue || newValue.match(/^\d*\.?\d{0,2}$/)) {
                      setLessAmount(parseFloat(newValue));
                    }
                  }}
                  value={isNaN(lessAmount) ? "" : lessAmount}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MuiTextField
                  type="number"
                  id="collectionamount"
                  label="Collection Amount"
                  onChange={(e) => {
                    //not allow to enter negative values - /^\d*\.?\d*$/
                    //not allow to enter negative values & allow only 2 digits after decimal - /^\d*\.?\d{0,2}$/
                    const newValue = e.target.value;
                    if (!newValue || newValue.match(/^\d*\.?\d{0,2}$/)) {
                      setCollectionAmount(parseFloat(newValue));
                    }
                  }}
                  value={isNaN(collectionAmount) ? "" : collectionAmount}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MuiTextField
                  type="number"
                  id="openingbalance"
                  label="Opening Balance"
                  InputProps={{
                    readOnly: true,
                  }}
                  onChange={(e) => {
                    setOpeningBalance(e.target.value);
                  }}
                  value={isNaN(openingBalance) ? "" : openingBalance}
                  autoComplete="off"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Paper>
      </Box>
    </>
  );
};

export default SummaryReport;
